import React, { useContext, Fragment, useEffect, useState } from 'react'
import { Col, Input, Typography, DatePicker } from 'components'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid, Divider } from '@mui/material'
import style from '../../deals.module.scss'
import { DealContext, UserContext } from 'context'
import { USER_ROLES } from 'helpers/constants'

const NewPreConCommission = ({ canDisabledCommission = false, primaryAgent, secondaryAgent, setAgentCommission, agentCommission }) => {
    const { user } = useContext(UserContext)
    const { control, watch, setValue, getValues } = useFormContext()
    const { deal, disabled = false } = useContext(DealContext)
    const [commissionErrors, setCommissionErrors] = useState({
        first: false,
        second: false,
        third: false,
        final: false
    })

    const installments = ['first', 'second', 'third', 'final']
    const watchFields = watch([
        'first_installment',
        'second_installment',
        'third_installment',
        'final_installment'
    ])

    const installmentTotal = watchFields.reduce((sum, v) => sum + (Number(v) || 0), 0)

    const getUniqueAgents = () => {
        const agents = []
        if (primaryAgent?.id) agents.push(primaryAgent)
        if (Array.isArray(secondaryAgent)) {
            agents.push(...secondaryAgent.filter(a => a?.id))
        }
        return agents.filter((a, i, arr) => i === arr.findIndex(x => x.id === a.id))
    }

    const uniqueAgents = getUniqueAgents()

    // Calculate installment amount when main amount or percentage changes
    useEffect(() => {
        installments.forEach(installment => {
            const mainAmount = getValues(`${installment}_installment`) || 0
            const percentage = getValues(`${installment}_installment_percentage`) || 0
            const amount = (mainAmount * percentage) / 100
            setValue(`${installment}_installment_amount`, amount.toFixed(2))
        })
    }, [
        watch('first_installment'),
        watch('second_installment'),
        watch('third_installment'),
        watch('final_installment'),
        watch('first_installment_percentage'),
        watch('second_installment_percentage'),
        watch('third_installment_percentage'),
        watch('final_installment_percentage')
    ])

    // Calculate agent amounts when installment amount or agent percentages change
    useEffect(() => {
        const newAgentCommission = {
            first: [],
            second: [],
            third: [],
            final: []
        };

        installments.forEach(installment => {
            const installmentAmount = getValues(`${installment}_installment_amount`) || 0;

            // Validate total agent percentage equals 100
            let totalAgentPercentage = 0;

            uniqueAgents.forEach(agent => {
                const agentPercentage = getValues(`${installment}_installment_agent_${agent.id}_percentage`) || 0;
                totalAgentPercentage += Number(agentPercentage);

                // Calculate agent amount
                const agentAmount = (installmentAmount * agentPercentage) / 100;
                setValue(`${installment}_installment_agent_${agent.id}_amount`, agentAmount.toFixed(2));

                // Add to agentCommission state
                newAgentCommission[installment].push({
                    agent_id: agent.id,
                    commission_percentage: agentPercentage,
                    commission_amount: agentAmount
                });
            });

            // Update error state for this specific installment
            setCommissionErrors(prev => ({
                ...prev,
                [installment]: totalAgentPercentage === 0 ? false : totalAgentPercentage > 100 || totalAgentPercentage < 100
            }));
        });

        // Update agentCommission state once
        setAgentCommission(newAgentCommission);
    }, [
        watch('first_installment_amount'),
        watch('second_installment_amount'),
        watch('third_installment_amount'),
        watch('final_installment_amount'),
        ...uniqueAgents.flatMap(agent => [
            watch(`first_installment_agent_${agent.id}_percentage`),
            watch(`second_installment_agent_${agent.id}_percentage`),
            watch(`third_installment_agent_${agent.id}_percentage`),
            watch(`final_installment_agent_${agent.id}_percentage`)
        ])
    ]);

    const handleAgentPercentageChange = (installment, agentId, value) => {
        const percentage = parseFloat(value) || 0

        // Get current percentages for all agents
        const currentPercentages = uniqueAgents.map(agent => ({
            id: agent.id,
            percentage: getValues(`${installment}_installment_agent_${agent.id}_percentage`) || 0
        }))

        // Find the current agent
        const otherAgents = currentPercentages.filter(p => p.id !== agentId)
        const totalOtherPercentages = otherAgents.reduce((sum, p) => sum + p.percentage, 0)

        // Validate that total doesn't exceed 100%
        if (percentage + totalOtherPercentages > 100) {
            // Update error for this specific installment
            setCommissionErrors(prev => ({
                ...prev,
                [installment]: true
            }))
            // Still update the value so user can see what they typed
            setValue(`${installment}_installment_agent_${agentId}_percentage`, percentage)

        } else {
            // Clear error for this installment
            setCommissionErrors(prev => ({
                ...prev,
                [installment]: false
            }))
            // Update the value
            setValue(`${installment}_installment_agent_${agentId}_percentage`, percentage)
        }
    }

    const renderInstallmentHeader = (title) => (
        <Col style={{ width: '100%' }}>
            <Typography.Heading
                type={6}
                style={{ marginTop: 22, fontFamily: 'Source Sans Pro' }}
                gap={50}
                className="dealStepTitle">
                {title}  {title === 'First Installment (Amount)' && (
                    <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                )}
            </Typography.Heading>
        </Col>
    )

    const renderInstallmentMeta = (prefix) => (
        <>
            <Grid container columnSpacing={7} sx={{ mt: 1 }}>
                <Grid xs={12} md={6} item>
                    <Controller
                        name={`${prefix}_firm_date`}
                        control={control}
                        defaultValue={deal?.preconDealInstallments?.[prefix]?.firm_date || null}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DatePicker
                                type={5}
                                error={error?.message}
                                value={value}
                                onChange={onChange}
                                label="Firm Date"
                                name={`${prefix}_firm_date`}
                                disabled={disabled || canDisabledCommission}
                                style={{ width: '100%' }}
                            />
                        )}
                    />
                </Grid>
                <Grid xs={12} md={6} item mt={{ xs: 3, md: 0 }}>
                    <Controller
                        name={`${prefix}_close_date`}
                        control={control}
                        defaultValue={deal?.preconDealInstallments?.[prefix]?.close_date || null}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DatePicker
                                type={5}
                                error={error?.message}
                                value={value}
                                onChange={onChange}
                                label="Close Date"
                                name={`${prefix}_close_date`}
                                disabled={disabled || canDisabledCommission}
                                style={{ width: '100%' }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Grid container sx={{ mt: 1 }}>
                <Controller
                    name={`${prefix}_settled_date`}
                    control={control}
                    defaultValue={deal?.preconDealInstallments?.[prefix]?.settled_date || null}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <DatePicker
                            type={5}
                            error={error?.message}
                            value={value}
                            onChange={onChange}
                            label="Settled Date"
                            name={`${prefix}_settled_date`}
                            disabled={disabled || canDisabledCommission}
                            style={{ width: '100%' }}
                        />
                    )}
                />
            </Grid>
        </>
    )

    const renderPercentageAmount = (prefix, isAgent = false, agentId = null, installmentName = null) => {
        // Helper function to get agent commission
        const getAgentCommission = (field) => {
            const extractInstallmentName = (str) => {
                const match = str.match(/(\w+)_?installment/i);
                return match ? (match[1] ? `${match[1]}installment` : 'installment') : str;
            };
            if (!agentId || !deal?.preconDealInstallments?.[extractInstallmentName(prefix)]?.agent_commission) return 0;

            // Find the specific agent's commission
            const agentCommission = deal.preconDealInstallments?.[extractInstallmentName(prefix)].agent_commission.find(
                commission => commission.agent_id === agentId
            );
            return agentCommission?.[field] || 0;
        };

        return (
            <Grid container columnSpacing={7}>
                <Grid xs={12} md={6} item>
                    <Controller
                        name={`${prefix}_percentage`}
                        control={control}
                        defaultValue={isAgent ?
                            getAgentCommission('commission_percentage') :
                            deal?.preconDealInstallments?.[prefix]?.percentage || 0
                        }
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                                type="number"
                                disabled={disabled || canDisabledCommission}
                                error={error?.message}
                                value={value}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    onChange(val);

                                    if (isAgent && agentId && installmentName) {
                                        handleAgentPercentageChange(installmentName, agentId, val);
                                    }
                                }}
                                label="Percentage"
                                name={`${prefix}_percentage`}
                                style={{ width: '100%', marginBottom: 10 }}
                            />
                        )}
                    />
                </Grid>
                <Grid xs={12} md={6} item mt={{ xs: 3, md: 0 }}>
                    <Controller
                        name={`${prefix}_amount`}
                        control={control}
                        defaultValue={isAgent ?
                            getAgentCommission('commission_amount') :
                            deal?.preconDealInstallments?.[prefix]?.amount || 0
                        }
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                                type="number"
                                disabled={true}
                                error={error?.message}
                                value={value}
                                onChange={(e) => {
                                    onChange(parseFloat(e.target.value));
                                }}
                                label="Amount"
                                name={`${prefix}_amount`}
                                style={{ width: '100%', marginBottom: 10 }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
        );
    };
    const renderInstallmentSection = (installmentName) => {
        const prefix = `${installmentName}_installment`
        const hasError = commissionErrors[installmentName]

        return (
            <>
                {renderInstallmentHeader(`${installmentName.charAt(0).toUpperCase() + installmentName.slice(1)} Installment (Amount)`)}

                <Controller
                    name={`${prefix}`}
                    control={control}
                    defaultValue={deal?.[prefix] || 0}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                            type="number"
                            disabled={disabled || canDisabledCommission}
                            error={error?.message}
                            value={value}
                            required
                            onChange={(e) => {
                                onChange(parseFloat(e.target.value))

                            }}
                            name={`${prefix}`}
                            style={{ width: '100%', marginBottom: 10 }}
                        />
                    )}
                />

                {renderInstallmentMeta(prefix)}
                {renderPercentageAmount(prefix)}

                {uniqueAgents.map(agent => (
                    <Fragment key={agent.id}>
                        <Typography.Subheading type={6} >
                            {agent.name}
                        </Typography.Subheading>
                        {renderPercentageAmount(
                            `${prefix}_agent_${agent.id}`,
                            true,
                            agent.id,
                            installmentName
                        )}
                    </Fragment>
                ))}

                {hasError && (
                    <Typography.Body type={1} style={{ color: 'red', marginTop: 10 }}>
                        Total percentage for {installmentName} installment should not exceed 100%.
                    </Typography.Body>
                )}

                {/* <Divider sx={{ my: 3 }} /> */}
            </>
        )
    }

    return (
        <div className={style.responsive}>
            <Col style={{ width: '100%', marginTop: 20 }}>
                <Typography.Heading
                    type={5}
                    style={{ marginTop: 22, fontFamily: 'Source Sans Pro' }}
                    gap={50}
                    className="dealStepTitle">
                    Commission Breakdown
                </Typography.Heading>
            </Col>
            <Col style={{ width: '100%', marginTop: 10 }}>
                <Controller
                    name="annual_personal_deal"
                    control={control}
                    defaultValue={deal ? (deal.annual_personal_deal ? 'yes' : 'no') : undefined}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <div className={style.annual_personal_deal_checkbox}>
                            <label htmlFor="annual_personal_deal_checkbox" className="annualDealSublabel">
                                This deal is for my personal property:
                            </label>
                            <div>
                                <input
                                    id="annual_personal_deal_checkbox"
                                    type="checkbox"
                                    value="yes"
                                    disabled={disabled || canDisabledCommission}
                                    checked={value === 'yes'}
                                    onChange={(e) => {
                                        onChange(e.target.checked ? 'yes' : 'no')
                                    }}
                                />
                                {error && <span>{error.message}</span>}
                            </div>
                        </div>
                    )}
                />
            </Col>

            {/* Render all installments */}
            {installments.map(installment => {
                // For second and third installments, check if user is not agent
                if ((installment === 'second' || installment === 'third') && user.role === USER_ROLES.agent) {
                    return null; // Don't render for agents
                }
                return renderInstallmentSection(installment);
            })}
            <Input
                type="number"
                label="Total(Amount)"
                name="total_commission"
                value={installmentTotal.toFixed(2)}
                disabled
                style={{ width: '100%' }}
            />
        </div>
    )
}

export default NewPreConCommission